// src/components/Newsboard/Newsboard.js
import React, { useState, useEffect, useRef } from 'react';
import { File, Send, Trash2, Edit2, X, Check, MessageSquare, ChevronLeft, ChevronRight, Pause, Play, Plus, Image } from 'lucide-react';
import io from 'socket.io-client';
import { convertMarkdownToHTML } from '../Utils/MDtoHTML';
import './Newsboard.css';

const Newsboard = ({ user, userProfile, setActiveTab }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [stats, setStats] = useState(null);
  const [pastedImages, setPastedImages] = useState([]);

  // 뉴스 롤링 관련 state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [inputMode, setInputMode] = useState('text'); // 'text', 'html', 'markdown'

  const socketRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';

  // Socket.IO 연결
  useEffect(() => {
    socketRef.current = io(serverUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('newsboard:new-message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    socketRef.current.on('newsboard:message-updated', (updatedMessage) => {
      setMessages(prev => prev.map(msg =>
        msg.id === updatedMessage.id ? updatedMessage : msg
      ));
    });

    socketRef.current.on('newsboard:message-deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(msg => {
        if (msg.id === messageId) return false;
        if (msg.parent_message_id === messageId) return false;
        return true;
      }));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [serverUrl]);

  // 초기 메시지 로드
  useEffect(() => {
    loadMessages();
    loadStats();
  }, []);

  // 원본 메시지(뉴스)만 필터링하고 최신순으로 정렬
  const rootMessages = messages
    .filter(msg => !msg.parent_message_id)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // 자동 롤링 효과
  useEffect(() => {
    if (rootMessages.length <= 1 || isPaused || showInput) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % rootMessages.length);
    }, 10000); // 10초

    return () => clearInterval(interval);
  }, [rootMessages.length, isPaused, showInput]);

  // 메시지 로드 
  const loadMessages = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${serverUrl}/api/newsboard/messages?limit=100`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessages(data.messages);
        }
      }
    } catch (error) {
      console.error('⌘ 메시지 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 통계 로드
  const loadStats = async () => {
    try {
      const response = await fetch(`${serverUrl}/api/newsboard/stats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setStats(data.stats);
      }
    } catch (error) {
      console.error('⌘ 통계 로드 실패:', error);
    }
  };

  // 텍스트를 HTML로 변환하는 헬퍼 함수 
  const convertToHTML = (text, mode) => {
    try {
      if (mode === 'html') {
        return text;
      } else if (mode === 'markdown') {
        return convertMarkdownToHTML(text);
      } else {
        return '<p>' + text.replace(/\n/g, '<br>') + '</p>';
      }
    } catch (error) {
      console.error('HTML 변환 오류:', error);
      return text;
    }
  };

  // 메시지 표시를 위한 파싱 함수
  const parseMessageForDisplay = (message) => {
    if (message.startsWith('$HTML$ ')) {
      return message.substring(7); // HTML은 그대로
    } else if (message.startsWith('$Markdown$ ')) {
      const content = message.substring(11);
      return convertToHTML(content, 'markdown'); // Markdown을 HTML로 변환
    } else {
      return convertToHTML(message, 'text'); // 일반 텍스트를 HTML로 변환
    }
  };

  // 이미지 붙여넣기 핸들러
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        addImageAtCursor(blob);
      }
    }
  };

  // 이미지 추가 헬퍼 함수 (공통 로직)
  const addImageAtCursor = (file) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const base64Image = event.target.result;
      const cursorPosition = textareaRef.current?.selectionStart || newMessage.length;

      // 이미지 플레이스홀더를 커서 위치에 삽입
      const placeholder = `[이미지${pastedImages.length + 1}]`;
      const newText =
        newMessage.slice(0, cursorPosition) +
        placeholder +
        newMessage.slice(cursorPosition);

      setNewMessage(newText);
      setPastedImages(prev => [...prev, { data: base64Image, placeholder }]);

      // 커서를 플레이스홀더 뒤로 이동
      setTimeout(() => {
        if (textareaRef.current) {
          const newPosition = cursorPosition + placeholder.length;
          textareaRef.current.selectionStart = newPosition;
          textareaRef.current.selectionEnd = newPosition;
          textareaRef.current.focus();
        }
      }, 0);
    };

    reader.readAsDataURL(file);
  };

  // 파일 선택 핸들러
  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach(file => {
      // 이미지 파일인 경우
      if (file.type.startsWith('image/')) {
        addImageAtCursor(file);
      }
      // 텍스트 파일인 경우 (txt, md, html 등)
      else if (file.type.startsWith('text/') || file.name.match(/\.(txt|md|markdown|html|htm)$/i)) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const fileContent = event.target.result;
          const cursorPosition = textareaRef.current?.selectionStart || newMessage.length;

          // 현재 커서 위치에 파일 내용 삽입
          const newText =
            newMessage.slice(0, cursorPosition) +
            fileContent +
            newMessage.slice(cursorPosition);

          setNewMessage(newText);

          // 커서를 삽입된 내용 끝으로 이동
          setTimeout(() => {
            if (textareaRef.current) {
              const newPosition = cursorPosition + fileContent.length;
              textareaRef.current.selectionStart = newPosition;
              textareaRef.current.selectionEnd = newPosition;
              textareaRef.current.focus();
            }
          }, 0);
        };
        reader.readAsText(file);
      }
      else {
        alert('이미지 또는 텍스트 파일만 선택할 수 있습니다.');
      }
    });

    // input 초기화 (같은 파일 다시 선택 가능하도록)
    e.target.value = '';
  };

  // 이미지 선택 버튼 클릭 핸들러
  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  // 이미지 제거 핸들러
  const removeImage = (index) => {
    setPastedImages(prev => {
      const imageToRemove = prev[index];
      // 텍스트에서 플레이스홀더 제거
      if (imageToRemove?.placeholder) {
        setNewMessage(current => current.replace(imageToRemove.placeholder, ''));
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // 메시지 전송 
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!user || !userProfile?.nickname) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!newMessage.trim() && pastedImages.length === 0) return;

    try {
      setIsSending(true);
      let originalContent = newMessage;

      // 이미지를 서버에 업로드하고 플레이스홀더를 URL로 교체
      if (pastedImages.length > 0) {
        let processedContent = originalContent;

        for (let i = 0; i < pastedImages.length; i++) {
          const imageData = pastedImages[i];
          try {
            // base64를 blob으로 변환
            const response = await fetch(imageData.data);
            const blob = await response.blob();

            // FormData로 이미지 업로드
            const formData = new FormData();
            formData.append('image', blob, `news-image-${Date.now()}-${i}.png`);
            formData.append('userEmail', user.email);

            const uploadResponse = await fetch(`${serverUrl}/api/newsboard/upload-image`, {
              method: 'POST',
              credentials: 'include',
              body: formData
            });

            const uploadData = await uploadResponse.json();
            if (uploadData.success && uploadData.imageUrl) {
              // 플레이스홀더를 이미지 태그로 교체
              const imageTag = `<img src="${uploadData.imageUrl}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
              processedContent = processedContent.replace(imageData.placeholder, imageTag);
            }
          } catch (uploadError) {
            console.error('이미지 업로드 실패:', uploadError);
            // 업로드 실패 시 플레이스홀더 제거
            processedContent = processedContent.replace(imageData.placeholder, '');
          }
        }

        originalContent = processedContent;
      }

      // inputMode에 따라 태그 추가 (원본 텍스트에 태그 추가)
      let messageToSend = originalContent;
      if (inputMode === 'html') {
        messageToSend = '$HTML$ ' + originalContent;
      } else if (inputMode === 'markdown') {
        messageToSend = '$Markdown$ ' + originalContent;
      }
      // inputMode === 'text'인 경우는 태그 없이 그대로

      const response = await fetch(`${serverUrl}/api/newsboard/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email,
          userNickname: userProfile.nickname,
          userPhotoUrl: userProfile.photoURL || null,
          message: messageToSend,
          parentMessageId: null,
          isPrivateThread: false
        })
      });

      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        setPastedImages([]);
        setShowInput(false);
        setCurrentIndex(0);
        await loadStats();
      } else {
        alert(data.error || '메시지 전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('⌘ 메시지 전송 실패:', error);
      alert('메시지 전송에 실패했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  // 메시지 삭제
  const handleDelete = async (messageId) => {
    if (!window.confirm('정말로 이 뉴스를 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`${serverUrl}/api/newsboard/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userEmail: user.email })
      });

      const data = await response.json();
      if (data.success) {
        if (currentIndex >= rootMessages.length - 1) {
          setCurrentIndex(Math.max(0, rootMessages.length - 2));
        }
        await loadStats();
      } else {
        alert(data.error || '삭제 실패');
      }
    } catch (error) {
      console.error('⌘ 삭제 실패:', error);
    }
  };

  // 메시지 수정
  const handleEdit = async () => {
    if (!editingText.trim()) return;

    try {
      // inputMode에 따라 태그 추가 (원본 텍스트에 태그 추가)
      let messageToUpdate = editingText;
      if (inputMode === 'html') {
        messageToUpdate = '$HTML$ ' + editingText;
      } else if (inputMode === 'markdown') {
        messageToUpdate = '$Markdown$ ' + editingText;
      }
      // inputMode === 'text'인 경우는 태그 없이 그대로

      const response = await fetch(`${serverUrl}/api/newsboard/messages/${editingMessageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userEmail: user.email,
          message: messageToUpdate
        })
      });

      const data = await response.json();
      if (data.success) {
        setEditingMessageId(null);
        setEditingText('');
      } else {
        alert(data.error || '수정 실패');
      }
    } catch (error) {
      console.error('⌘ 수정 실패:', error);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + rootMessages.length) % rootMessages.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % rootMessages.length);
  };

  const isMessageOwner = (message) => user?.email === message.user_email;
  const isAdmin = ['gategil1149@gmail.com', 'worldlandcafe@gmail.com'].includes(user?.email);
  const isINSSA = user?.level >= 4;

  console.log('user', user);

  console.log('isINSSA', isINSSA);

  const currentMessage = rootMessages[currentIndex];

  return (
    <div className="newsboard-container">
      <div className="newsboard-header">
        <div className="newsboard-title">
          <MessageSquare className="title-icon" />
          <h3>WorldLand Highlights</h3>
        </div>
        <div className="newsboard-controls">
          {user && isINSSA && (
            <button
              className={`btn-toggle-input ${showInput ? 'active' : ''}`}
              onClick={() => setShowInput(!showInput)}
            >
              {showInput ? <X size={18} /> : <Plus size={18} />}
              <span>{showInput ? '닫기' : 'News 입력'}</span>
            </button>
          )}

          {setActiveTab && (
            <button
              className="btn-go-chat-newboard"
              onClick={() => setActiveTab('board')}
            >
              {/* <MessageSquare size={18} /> */}
              <span>잡담하러 가기</span>
            </button>
          )}
        </div>
      </div>

      {showInput && (
        <div className="news-input-area">
          <div className="input-mode-tabs">
            <button
              className={`mode-tab ${inputMode === 'text' ? 'active' : ''}`}
              onClick={() => setInputMode('text')}
            >
              텍스트
            </button>
            <button
              className={`mode-tab ${inputMode === 'html' ? 'active' : ''}`}
              onClick={() => setInputMode('html')}
            >
              HTML
            </button>
            <button
              className={`mode-tab ${inputMode === 'markdown' ? 'active' : ''}`}
              onClick={() => setInputMode('markdown')}
            >
              마크다운
            </button>
          </div>
          <form onSubmit={handleSendMessage}>
            <div className="textarea-wrapper">
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onPaste={handlePaste}
                placeholder={
                  inputMode === 'text' ? '새로운 소식을 입력하세요 (이미지 붙여넣기 가능)...' :
                    inputMode === 'html' ? 'HTML 코드를 입력하세요 (이미지 붙여넣기 가능)...' :
                      '마크다운 형식으로 입력하세요 (이미지 붙여넣기 가능)...'
                }
              // maxLength={5000}
              />
              <button
                type="button"
                className="btn-add-image"
                onClick={handleImageButtonClick}
                title="파일 선택"
              >
                <Image size={20} /> <File size={20} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,text/*,.txt,.md,.markdown,.html,.htm"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
            {pastedImages.length > 0 && (
              <div className="pasted-images-preview">
                {pastedImages.map((imageData, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={imageData.data} alt={`pasted-${index}`} />
                    <div className="image-placeholder-label">{imageData.placeholder}</div>
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="input-actions">
              <span className="char-count">
                {newMessage.length}자 {pastedImages.length > 0 && `/ 이미지 ${pastedImages.length}개`}
              </span>
              <button type="submit" className="btn-send" disabled={isSending || (!newMessage.trim() && pastedImages.length === 0)}>
                {isSending ? '등록 중...' : '등록'} <Send size={16} />
              </button>
            </div>
          </form>
        </div>
      )}

      <div
        className="news-display-area"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {isLoading ? (
          <div className="news-loading">
            <div className="spinner"></div>
            <p>뉴스를 불러오는 중...</p>
          </div>
        ) : rootMessages.length > 0 && currentMessage ? (
          <div className="news-card-single fade-in" key={currentMessage.id}>
            <div className="news-content-wrapper">
              <div className="news-header-info">
                <span className="news-date">
                  {new Date(currentMessage.created_at).toLocaleDateString()}
                </span>
                <span className="news-author">
                  by {currentMessage.user_nickname}
                </span>
              </div>

              {editingMessageId === currentMessage.id ? (
                <div className="news-edit-mode">
                  <div className="input-mode-tabs">
                    <button
                      className={`mode-tab ${inputMode === 'text' ? 'active' : ''}`}
                      onClick={() => setInputMode('text')}
                    >
                      텍스트
                    </button>
                    <button
                      className={`mode-tab ${inputMode === 'html' ? 'active' : ''}`}
                      onClick={() => setInputMode('html')}
                    >
                      HTML
                    </button>
                    <button
                      className={`mode-tab ${inputMode === 'markdown' ? 'active' : ''}`}
                      onClick={() => setInputMode('markdown')}
                    >
                      마크다운
                    </button>
                  </div>
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="edit-textarea"
                    placeholder={
                      inputMode === 'text' ? '텍스트를 입력하세요...' :
                        inputMode === 'html' ? 'HTML 코드를 입력하세요...' :
                          '마크다운 형식으로 입력하세요...'
                    }
                  />
                  <div className="edit-buttons">
                    <button onClick={handleEdit} className="btn-save">저장</button>
                    <button onClick={() => setEditingMessageId(null)} className="btn-cancel">취소</button>
                  </div>
                </div>
              ) : (
                <div className="news-body">
                  <iframe
                    className="html-preview"
                    title="HTML Preview"
                    srcDoc={parseMessageForDisplay(currentMessage.message)}
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
              )}
            </div>

            {(isMessageOwner(currentMessage) || isAdmin) && !editingMessageId && (
              <div className="news-actions">
                <button
                  onClick={() => {
                    let textToEdit = currentMessage.message;
                    let detectedMode = 'text';

                    if (textToEdit.startsWith('$HTML$ ')) {
                      textToEdit = textToEdit.substring(7);
                      detectedMode = 'html';
                    } else if (textToEdit.startsWith('$Markdown$ ')) {
                      textToEdit = textToEdit.substring(11);
                      detectedMode = 'markdown';
                    }

                    setEditingMessageId(currentMessage.id);
                    setEditingText(textToEdit);
                    setInputMode(detectedMode);
                  }}
                  className="btn-action edit"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(currentMessage.id)}
                  className="btn-action delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="news-empty">
            <p>등록된 뉴스가 없습니다.</p>
          </div>
        )}

        {rootMessages.length > 1 && (
          <>
            <button className="nav-btn prev" onClick={handlePrev}>
              <ChevronLeft size={24} />
            </button>
            <button className="nav-btn next" onClick={handleNext}>
              <ChevronRight size={24} />
            </button>

            <div className="news-indicators">
              {rootMessages.map((_, idx) => (
                <button
                  key={idx}
                  className={`indicator-dot ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Newsboard;